import { useUpdateEffect } from 'ahooks';
import { uniqueId } from 'lodash-es';
import React, { KeyboardEvent, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { rtDebug } from '.';
import { useAppSelector } from '../../../../hooks/hooks';
import useRenderDebug from '../../../../hooks/useRenderDebug';
import { TApiaFilter } from '../../../../types';
import { customEvents } from '../../../../utils/dom';
import persistentStorage from '../../../../utils/persistentStorage';
import useOtherTagButton from '../../buttons/useOtherTagButton';
import Icon from '../../Icon';
import FilterCell from './FilterCell';
import HeaderCell from './HeaderCell';
import { useResponsiveTableContext } from './ResponsiveTableContext';
import StateCell from './StateCell';
import { responsiveTableActions } from '../../../../store/responsiveTableSlice';
import store from '../../../../store';
import { Input } from '@apia/theme';

declare global {
  interface Window {
    focusedRow: number;
    focusedColumn: number;
    hasNonAdditionalFilters: boolean;
  }
}

const Header = () => {
  const { name, ...rest } = useResponsiveTableContext();
  const { columns, hasNonAdditionalFilters, focusedColumn, focusedRow } =
    useAppSelector((global) => {
      const tableState = global.responsiveTableSlice[name];
      window.focusedRow = tableState?.focusedRow;
      window.focusedColumn = tableState?.focusedColumn;
      window.hasNonAdditionalFilters =
        tableState?.hasNonAdditionalFilters || false;
      return {
        columns: tableState?.columns ?? [],
        focusedColumn: tableState?.focusedColumn,
        focusedRow: tableState?.focusedRow,
        hasNonAdditionalFilters: tableState?.hasNonAdditionalFilters,
      };
    }, shallowEqual);

  const filters = useAppSelector<
    { columnIndex: number; definition: TApiaFilter }[]
  >(
    (global) => {
      const tableState = global.responsiveTableSlice[name];
      const returnObject = (tableState?.filters ?? [])
        .map((current) => ({
          columnIndex: tableState.columns.findIndex(
            (search) => search.name === current.column,
          ),
          definition: current,
        }))
        .filter((current) => current.columnIndex >= 0);

      return returnObject;
    },
    React.useMemo(
      () =>
        (
          a: { columnIndex: number; definition: TApiaFilter }[],
          b: { columnIndex: number; definition: TApiaFilter }[],
        ) => {
          return (
            a.length === b.length &&
            a.reduce<boolean>((result, current, index) => {
              return (
                result && shallowEqual(current.definition, b[index].definition)
              );
            }, true)
          );
        },
      [],
    ),
  );

  const states = useAppSelector((globalState) => {
    const state = globalState.responsiveTableSlice[name];
    return state?.statesColumns ?? [];
  }, shallowEqual);

  useRenderDebug(rtDebug.header, 'Header.tsx', {
    columns,
    ...rest,
  });

  const additionalColumns = React.useMemo(
    () =>
      columns.reduce(
        (count, column) => count + (column.showAsAdditional ? 1 : 0),
        0,
      ),
    [columns],
  );

  const statesWithIds = React.useMemo(() => {
    return states.map((current) => ({
      ...current,
      id: uniqueId(),
    }));
  }, [states]);

  const isFiltersRowVisible = useAppSelector((globalState) => {
    const state = globalState.responsiveTableSlice[name];

    return state?.isFiltersRowOpen ?? false;
  }, shallowEqual);

  React.useEffect(() => {
    persistentStorage.showResponsiveTableFilters = isFiltersRowVisible;
  }, [isFiltersRowVisible]);

  const buttonProps = useOtherTagButton(
    React.useMemo(
      () => ({
        className: 'additionalColumn',
        onKeyDown: (ev: KeyboardEvent) => {
          if (ev.code === 'Enter') {
            ev.stopPropagation();
          }
        },
        onClick: () => {
          const hasAdditionalFilters = store
            .getState()
            .responsiveTableSlice[name].filters.some((f) => !f.asAdditional);
          if (hasAdditionalFilters) {
            store.dispatch(
              responsiveTableActions.toggleFiltersRow({ tableName: name }),
            );
          }
        },
      }),
      [name],
    ),
  );

  const [ref, setRef] = useState<HTMLTableRowElement | null>(null);

  useUpdateEffect(() => {
    const handleFocus = (ev: Event) => {
      ev.stopPropagation();
      store.dispatch(
        responsiveTableActions.toggleFiltersRow({
          tableName: name,
          isOpen: true,
        }),
      );
    };
    ref?.addEventListener(customEvents.focus, handleFocus);
    return () => {
      ref?.removeEventListener(customEvents.focus, handleFocus);
    };
  }, [ref]);

  return (
    <thead>
      <tr data-focusindex={1}>
        {(additionalColumns > 0 || hasNonAdditionalFilters) && (
          <th
            data-colindex={1}
            aria-rowindex={1}
            data-focused={
              focusedColumn === 1 &&
              focusedRow === -1 - (hasNonAdditionalFilters ? 1 : 0)
            }
            {...buttonProps}
            tabIndex={
              focusedColumn === 1 &&
              focusedRow === -1 - (hasNonAdditionalFilters ? 1 : 0)
                ? 0
                : -1
            }
            role="rowheader"
          >
            {hasNonAdditionalFilters ? (
              <Icon
                className="filterButton"
                name="Filter"
                title={
                  isFiltersRowVisible
                    ? window.LBL_HIDE_FILTERS
                    : window.LBL_SHOW_FILTERS
                }
                aria-label={
                  isFiltersRowVisible
                    ? window.LBL_HIDE_FILTERS
                    : window.LBL_SHOW_FILTERS
                }
              />
            ) : (
              <>&nbsp;</>
            )}
          </th>
        )}
        {statesWithIds.map((current, i) => (
          <StateCell
            isHeader
            key={current.id}
            index={i}
            rowIndex={1}
            state={current}
          />
        ))}
        {columns
          .map((current, actualColumnIndex) => ({
            ...current,
            actualTableColumnIndex: actualColumnIndex,
          }))
          .filter((current) => !current.showAsAdditional)
          .map((current, columnIndex) => {
            return (
              <HeaderCell
                apiaColumnIndex={current.actualTableColumnIndex}
                columnIndex={current.actualTableColumnIndex}
                inTableColumnIndex={columnIndex}
                key={current.name}
              />
            );
          })}
        <td /** This is a hack for css */
          role="presentation"
          sx={{
            width: '0 !important',
            maxWidth: '0 !important',
            minWidth: '0 !important',
            overflow: 'hidden !important',
            padding: '0 !important',
          }}
        />
      </tr>
      {hasNonAdditionalFilters && (
        <tr
          ref={setRef}
          data-focusindex={2}
          className={`responsiveTable__filters__row ${
            isFiltersRowVisible ? '' : 'hidden'
          }`}
        >
          {(additionalColumns > 0 || hasNonAdditionalFilters) && (
            <td
              className="noFilter"
              data-colindex={1}
              aria-rowindex={2}
              tabIndex={focusedColumn === 1 && focusedRow === -1 ? 0 : -1}
              data-focused={focusedColumn === 1 && focusedRow === -1}
            >
              <Input disabled />
            </td>
          )}
          {statesWithIds.map(({ id }, i) => (
            <StateCell isHeader key={id} index={i} rowIndex={2} />
          ))}
          {columns
            .filter((current) => !current.showAsAdditional)
            .map((current, columnIndex) => {
              const filter = filters.find(
                (search) => search.definition.column === current.name,
              );

              return current.showAsAdditional ? null : (
                <FilterCell
                  key={current.name}
                  columnIndex={columnIndex}
                  filter={filter?.definition}
                />
              );
            })}
        </tr>
      )}
    </thead>
  );
};

export default React.memo(Header);
